<script setup>
import { ref, computed, nextTick } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useVModel } from '@vueuse/core'

import { useDetectDevice } from '@/composables/device'
import { useWebSiteStore, searchResultTypeEnum } from '@/stores/web-site.store'

import { useEquipmentQrScan } from '@/composables/qrcode'
import QrcodeScanner from '@/components/shared/QrcodeScanner.vue'
import TypewriterText from '@/components/shared/TypewriterText.vue'

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const webSiteStore = useWebSiteStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:modelValue'])
const open = useVModel(props, 'modelValue', emit)

const MIN_QUERY_LENGTH = 2
const SEARCH_HINT_KEYS = ['common.searchHint1', 'common.searchHint2', 'common.searchHint3', 'common.searchHint4', 'common.searchHint5']

const query = ref('')
const searchHints = computed(() => SEARCH_HINT_KEYS.map((key) => i18n.t(key)))
const results = ref([])
const loading = ref(false)
const searched = ref(false)
const highlightedIndex = ref(-1)
const resultsScroll = ref(null)
let lastQuery = ''


let queryDebounceTimer = null
const onQueryInput = (value) => {
  query.value = value ?? ''
  clearTimeout(queryDebounceTimer)
  searched.value = false
  if (query.value.trim().length < MIN_QUERY_LENGTH) {
    results.value = []
    loading.value = false
    return
  }
  loading.value = true
  queryDebounceTimer = setTimeout(() => onQueryUpdate(query.value), webSiteStore.getInputDebounce())
}

const onQueryUpdate = async (value) => {
  const trimmed = (value ?? '').trim()
  if (trimmed.length < MIN_QUERY_LENGTH) {
    results.value = []
    loading.value = false
    searched.value = false
    return
  }
  loading.value = true
  lastQuery = trimmed
  const menuResults = webSiteStore.searchMenuEntries(trimmed, isMobile.value)
  const data = await webSiteStore.searchAsync(trimmed)
  if (lastQuery !== trimmed) return
  results.value = [...menuResults, ...data]
  highlightedIndex.value = results.value.length ? 0 : -1
  loading.value = false
  searched.value = true
}

const { isScanning, onQrcodeDetected, onQrcodeScanError } = useEquipmentQrScan((equipment) => {
  onQueryInput(equipment.name || equipment.code || '')
})

const scrollHighlightedIntoView = async () => {
  await nextTick()
  const items = resultsScroll.value?.querySelectorAll('.result-item')
  items?.[highlightedIndex.value]?.scrollIntoView({ block: 'nearest' })
}

const moveHighlight = (delta) => {
  const count = results.value.length
  if (!count) return
  highlightedIndex.value = (highlightedIndex.value + delta + count) % count
  scrollHighlightedIntoView()
}

const onEnter = () => {
  const result = results.value[highlightedIndex.value]
  if (result) openResult(result)
}

const highlight = (text) => {
  const value = text ?? ''
  const needle = query.value.trim().toLowerCase()
  if (!needle) return [{ text: value, hit: false }]
  const lowered = value.toLowerCase()
  const segments = []
  let cursor = 0
  while (cursor < value.length) {
    const index = lowered.indexOf(needle, cursor)
    if (index === -1) {
      segments.push({ text: value.slice(cursor), hit: false })
      break
    }
    if (index > cursor) segments.push({ text: value.slice(cursor, index), hit: false })
    segments.push({ text: value.slice(index, index + needle.length), hit: true })
    cursor = index + needle.length
  }
  return segments
}

const typeConfig = (type) => webSiteStore.getSearchResultTypeConfig(type)
const typeLabel = (type) => i18n.t(typeConfig(type).labelKey, 1)
const resultSubtitle = (result) =>
  result.type === searchResultTypeEnum.WAREHOUSE_MOVEMENT
    ? i18n.t('common.recentMovements', { count: result.count, days: result.days })
    : result.subtitle

const openResult = async (result) => {
  open.value = false
  await webSiteStore.openSearchResultAsync(result)
}

const onSearchHide = () => {
  query.value = ''
  results.value = []
  highlightedIndex.value = -1
  loading.value = false
  searched.value = false
}
</script>

<template>
  <div>
    <q-dialog
      v-model="open"
      :position="isMobile ? 'standard' : 'top'"
      @hide="onSearchHide"
    >
      <q-card
        class="search-card"
        :style="isMobile ? 'width: 95vw; max-width: 95vw; border-radius: 0.875rem;' : 'width: 40rem; max-width: 90vw; margin-top: 7.5rem; max-height: calc(100vh - 9rem); border-radius: 0.875rem; box-shadow: 0 1.25rem 3.75rem rgba(0, 0, 0, 0.35);'"
      >
        <q-card-section class="row items-center no-wrap q-py-sm search-input-row">
          <q-icon
            name="mdi-magnify"
            size="sm"
            class="q-mr-sm text-grey-6"
          />
          <q-input
            :model-value="query"
            autofocus
            borderless
            clearable
            dense
            class="col"
            @update:model-value="onQueryInput"
            @keydown.down.prevent="moveHighlight(1)"
            @keydown.up.prevent="moveHighlight(-1)"
            @keydown.enter.prevent="onEnter"
          />
          <q-btn
            flat
            round
            icon="mdi-qrcode-scan"
            size="md"
            class="q-ml-sm text-grey-6"
            @click="isScanning = true"
          />
          <q-spinner
            v-if="loading"
            size="sm"
            color="primary"
            class="q-ml-sm"
          />
        </q-card-section>
        <q-separator />

        <div
          ref="resultsScroll"
          class="results-scroll"
        >
          <div
            v-if="query.trim().length < MIN_QUERY_LENGTH"
            class="empty-state text-grey-6"
          >
            <typewriter-text :phrases="searchHints" />
          </div>
          <div
            v-else-if="searched && !loading && results.length === 0"
            class="empty-state text-grey-6"
          >
            {{ i18n.t('common.noResults') }}
          </div>
          <template v-else-if="results.length > 0">
            <div class="results-count text-grey-6">
              {{ i18n.t('common.searchResultsCount', { count: results.length }) }}
            </div>
            <q-list>
              <q-item
                v-for="(result, index) in results"
                :key="`${result.type}-${result.id}`"
                v-ripple
                clickable
                class="result-item"
                :class="{ 'result-active': index === highlightedIndex }"
                @click="openResult(result)"
                @mousemove="highlightedIndex = index"
              >
                <q-item-section avatar>
                  <q-avatar
                    :color="typeConfig(result.type).bgColor"
                    :text-color="typeConfig(result.type).textColor"
                    size="2.25rem"
                    :icon="result.icon || typeConfig(result.type).icon"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="result-title">
                    <span
                      v-for="(segment, index) in highlight(result.title)"
                      :key="index"
                      :class="{ 'search-hl': segment.hit }"
                    >{{ segment.text }}</span>
                  </q-item-label>
                  <q-item-label
                    v-if="resultSubtitle(result)"
                    caption
                    lines="1"
                  >
                    <span
                      v-for="(segment, index) in highlight(resultSubtitle(result))"
                      :key="index"
                      :class="{ 'search-hl': segment.hit }"
                    >{{ segment.text }}</span>
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-chip
                    :color="typeConfig(result.type).bgColor"
                    :text-color="typeConfig(result.type).textColor"
                    dense
                    class="type-chip"
                  >
                    {{ typeLabel(result.type) }}
                  </q-chip>
                </q-item-section>
              </q-item>
            </q-list>
          </template>
        </div>
      </q-card>
    </q-dialog>

    <q-dialog
      maximized
      :model-value="isScanning"
    >
      <qrcode-scanner
        v-if="isScanning"
        :timeout="5000"
        @detect="onQrcodeDetected"
        @timeout="onQrcodeScanError"
        @error="onQrcodeScanError"
      />
    </q-dialog>
  </div>
</template>

<style scoped lang="scss">
.search-card {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
}
.search-input-row {
  padding: 1.125rem 1.375rem;
}
.search-input-row :deep(.q-field__control) {
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}
.search-input-row :deep(.q-field__control)::before,
.search-input-row :deep(.q-field__control)::after {
  display: none;
}
.search-input-row :deep(.q-field__native) {
  font-size: 1.0625rem;
  color: #1d1d1d;
  outline: none;
}
.results-scroll {
  overflow-y: auto;
  flex: 1;
}
.empty-state {
  padding: 2rem 1rem;
  text-align: center;
}
.results-count {
  padding: 0.625rem 1.25rem 0.25rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
}
.result-item {
  padding: 0.75rem 1.25rem;
}
.result-item:hover,
.result-active {
  background: rgba($primary, 0.08);
}
.result-title {
  font-weight: 500;
  font-size: 0.90625rem;
  color: #1d1d1d;
}
.result-item :deep(.q-item__label--caption) {
  font-size: 0.78125rem;
  color: #757575;
}
.search-hl {
  color: $primary;
  font-weight: 700;
  background: rgba($primary, 0.1);
  border-radius: 0.1875rem;
  padding: 0 0.0625rem;
}
.type-chip {
  text-transform: capitalize;
  font-size: 0.65625rem;
  font-weight: 600;
  letter-spacing: 0.0125rem;
  border-radius: 0.5625rem;
}
</style>
